import { useState } from "react";
import classNames from "classnames";
import { StaticImageData } from "next/image";
import { If } from "react-conditional-components-renderer";

import AscDropdown from "../AscDropdown/AscDropdown";
import AscTag from "../AscTag/AscTag";
import AscUserDropdown from "../AscUserDropdown/AscUserDropdown";
import styles from "./AscTopNavigation.module.scss";
import AscButton, { AscButtonProps } from "../AscButton/AscButton";
import { ArrowLeftIcon, MenuIcon } from "../AscIcons";
import AscMenuIconButton from "../AscButton/AscMenuIconButton/AscMenuIconButton";
import AscLogo from "../AscLogo/AscLogo";
import { AscDropdownOptionProps } from "../AscDropdown/AscDropdownOptions/AscDropdownOptions";
import { usePlatformSidebar } from "../AscPlatformLayout/AscPlatformLayoutContext/usePlatformSidebar";
import { roundAndDropTrailingZeros } from "@ascendnow/shared";
import AscNotificationDrawer from "../AscNotificationDrawer/AscNotificationDrawer";

export interface AscTopNavigationProps {
  title?: string;
  subTitle?: string;
  notificationsCount?: number;
  contained?: boolean;
  showBackButton?: boolean;
  onBackButtonClick?: AscButtonProps["onClick"];
  backButtonIcon?: React.ReactNode;
  backButtonLabel?: string;
  showLogo?: boolean;
  className?: string;
  loading?: boolean;
  name: string;
  avatarUrl: string | StaticImageData;
  globalRank?: number;
  globalRankUrl?: string;
  classRank?: number;
  classRankUrl?: string;
  points?: number;
  coinsCount?: number;
  userDropdownOptions?: AscDropdownOptionProps[];
  onLogout?: () => void;
}

export default function AscTopNavigation({
  title,
  subTitle,
  coinsCount,
  notificationsCount = 0,
  avatarUrl,
  name,
  contained,
  showBackButton,
  onBackButtonClick,
  backButtonIcon,
  backButtonLabel,
  className,
  globalRank,
  classRank,
  points,
  showLogo = true,
  userDropdownOptions = [],
  onLogout,
  globalRankUrl,
  classRankUrl,
}: AscTopNavigationProps) {
  const [showNotifications, setShowNotifications] = useState(false);
  const { expandSidebar } = usePlatformSidebar();
  return (
    <nav
      data-testid="asc-top-navigation"
      className={classNames(
        styles.ground,
        {
          [styles.contained]: contained,
        },
        className
      )}
    >
      <div
        className={classNames(styles.leftContent, {
          [styles.withLogo]: !title && !showBackButton && showLogo,
          [styles.withBackButton]: showBackButton,
          [styles.withTitle]: title,
        })}
      >
        <AscLogo
          wrapperClassName={styles.logoWrapper}
          className={styles.logo}
        />
        <AscButton
          size="small"
          variant="lightGray"
          className={styles.desktopBackButton}
          icon={backButtonIcon || <ArrowLeftIcon />}
          onClick={onBackButtonClick}
        >
          {backButtonLabel || "Back"}
        </AscButton>
        <AscButton
          size="small"
          variant="lightGray"
          className={styles.mobileBackButton}
          icon={backButtonIcon || <ArrowLeftIcon />}
          onClick={onBackButtonClick}
        />
        <div className={styles.titleContainer}>
          <If test={!!title}>
            <h3 className={styles.title}>{title}</h3>
          </If>
          <If test={!!subTitle}>
            <p className={styles.subTitle}>{subTitle}</p>
          </If>
        </div>
      </div>

      <div className={styles.rightContent}>
        <AscTag
          type="coin"
          label={roundAndDropTrailingZeros(coinsCount ?? 0)}
          className={styles.tag}
        />
        <div className={styles.separator} />
        <AscMenuIconButton
          className={classNames(styles.notificationButton, {
            [styles.hideNotificationButtonOnMobile]: showBackButton,
          })}
          count={notificationsCount}
          onClick={() => setShowNotifications((prev) => !prev)}
        />
        <AscNotificationDrawer
          setVisible={setShowNotifications}
          visible={showNotifications}
        />
        <AscUserDropdown
          avatarUrl={typeof avatarUrl === "string" ? avatarUrl : avatarUrl?.src}
          alignment="right"
          name={name}
          globalRank={globalRank}
          classRank={classRank}
          points={points}
          globalRankUrl={globalRankUrl}
          classRankUrl={classRankUrl}
          className={styles.userDropdown}
          innerContainerClassName={styles.userDropdownInnerContainer}
          nameClassName={styles.userName}
          onLogout={onLogout}
        >
          {userDropdownOptions?.map((option, index) => (
            <AscDropdown.Option
              className={styles.dropdown}
              key={index}
              {...option}
            />
          ))}
        </AscUserDropdown>
        <AscMenuIconButton
          className={styles.hamburgerMenuButton}
          onClick={expandSidebar}
        >
          <MenuIcon />
        </AscMenuIconButton>
      </div>
    </nav>
  );
}
